/**
 * Annotation helper utilities for external diffs
 * Calls management API to update annotation coordinates and invalidation status
 */

// Management API configuration
const MANAGEMENT_API_URL = import.meta.env.VITE_PRICINGSAAS_MANAGEMENT_API_URL ||
  'https://nyqqynbtcrqdmhezmhsp.supabase.co/functions/v1/management';
const MANAGEMENT_API_KEY = import.meta.env.VITE_PRICINGSAAS_MANAGEMENT_API_KEY || '';

export interface AnnotationMarker {
  x: number;
  y: number;
}

export interface ViewportState {
  zoom: number;
  panX: number;
  panY: number;
}

export interface MarkerData {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  hideMarker1?: boolean;
  hideMarker2?: boolean;
}

export interface EventAnnotation {
  type?: 'comparison' | 'before_only' | 'after_only';
  compare_viewer_url?: string;
  before_viewer_url?: string;
  after_viewer_url?: string;
  before_marker?: AnnotationMarker;
  after_marker?: AnnotationMarker;
  before_cropped_urls?: string[];
  after_cropped_urls?: string[];
  before_annotated_urls?: string[];
  after_annotated_urls?: string[];
  cloudinary_urls?: {
    primary_original_url: string;
    secondary_original_url: string;
    primary_cropped_url: string;
    secondary_cropped_url: string;
  };
  // New fields for multi-marker support
  markers?: MarkerData[];
  viewport1?: ViewportState;
  viewport2?: ViewportState;
}

export interface UpdateEventAnnotationParams {
  eventId: string;
  annotation?: EventAnnotation | null;
  invalid?: boolean;
  description?: string;
  type?: string; // Event type (e.g., 'price_increased', 'plan_added')
}

export interface ManagementApiResponse {
  success: boolean;
  message?: string;
  data?: any;
  error?: string;
}

/**
 * Get authentication headers for management API
 */
function getManagementHeaders(): HeadersInit {
  if (!MANAGEMENT_API_KEY) {
    throw new Error('VITE_PRICINGSAAS_MANAGEMENT_API_KEY environment variable is not set');
  }

  return {
    'X-API-Key': MANAGEMENT_API_KEY,
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };
}

/**
 * Update event annotation via management API
 * PATCH /management/events/{event-id}
 */
export async function updateEventAnnotation(
  params: UpdateEventAnnotationParams
): Promise<ManagementApiResponse> {
  const { eventId, annotation, invalid, description, type } = params;

  console.log('🔄 [Annotations] Updating event annotation via management API:', eventId);

  const url = `${MANAGEMENT_API_URL}/events/${eventId}`;
  const body: any = {};

  if (annotation !== undefined) {
    body.annotation = annotation;
  }

  if (invalid !== undefined) {
    body.invalid = invalid;
  }

  if (description !== undefined) {
    body.description = description;
  }

  if (type !== undefined) {
    body.type = type;
  }

  try {
    const response = await fetch(url, {
      method: 'PATCH',
      headers: getManagementHeaders(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Management API request failed with status ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ [Annotations] Successfully updated event annotation');

    return {
      success: true,
      message: 'Event annotation updated successfully',
      data,
    };
  } catch (error) {
    console.error('❌ [Annotations] Failed to update event annotation:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

/**
 * Update only the marker positions (convenience method)
 */
export async function updateEventMarkers(params: {
  eventId: string;
  beforeMarker?: AnnotationMarker;
  afterMarker?: AnnotationMarker;
  annotationType?: 'comparison' | 'before_only' | 'after_only';
}): Promise<ManagementApiResponse> {
  const { eventId, beforeMarker, afterMarker, annotationType = 'comparison' } = params;

  console.log('📍 [Annotations] Updating event markers:', {
    eventId,
    beforeMarker,
    afterMarker,
  });

  // Build annotation object with updated markers
  const annotation: EventAnnotation = {
    type: annotationType,
  };

  if (beforeMarker) {
    annotation.before_marker = beforeMarker;
  }

  if (afterMarker) {
    annotation.after_marker = afterMarker;
  }

  return updateEventAnnotation({
    eventId,
    annotation,
  });
}

/**
 * Mark event as invalid
 */
export async function markEventAsInvalid(
  eventId: string,
  invalid: boolean = true
): Promise<ManagementApiResponse> {
  console.log(`${invalid ? '❌' : '✅'} [Annotations] Marking event as ${invalid ? 'invalid' : 'valid'}:`, eventId);

  return updateEventAnnotation({
    eventId,
    invalid,
  });
}

/**
 * Update event description
 */
export async function updateEventDescription(
  eventId: string,
  description: string
): Promise<ManagementApiResponse> {
  console.log('✏️ [Annotations] Updating event description:', eventId);

  return updateEventAnnotation({
    eventId,
    description,
  });
}

/**
 * Update event type
 */
export async function updateEventType(
  eventId: string,
  type: string
): Promise<ManagementApiResponse> {
  console.log('🏷️ [Annotations] Updating event type:', eventId, type);

  return updateEventAnnotation({
    eventId,
    type,
  });
}

/**
 * Clear event annotation (set to null) - DEPRECATED
 * Use resetEventAnnotation() instead to properly reset to system defaults
 */
export async function clearEventAnnotation(
  eventId: string
): Promise<ManagementApiResponse> {
  console.log('🗑️ [Annotations] Clearing event annotation (deprecated - use resetEventAnnotation):', eventId);

  return updateEventAnnotation({
    eventId,
    annotation: null,
  });
}

/**
 * Reset event annotation to system defaults
 * DELETE /management/events/{event-id}/annotation
 */
export async function resetEventAnnotation(
  eventId: string
): Promise<ManagementApiResponse> {
  console.log('🔄 [Annotations] Resetting event annotation to defaults:', eventId);

  const url = `${MANAGEMENT_API_URL}/events/${eventId}/annotation`;

  try {
    const response = await fetch(url, {
      method: 'DELETE',
      headers: getManagementHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Management API request failed with status ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ [Annotations] Successfully reset event annotation to defaults');

    return {
      success: true,
      message: 'Event annotation reset to system defaults',
      data,
    };
  } catch (error) {
    console.error('❌ [Annotations] Failed to reset event annotation:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

/**
 * Save event annotation with multi-marker and viewport support
 * POST /management/events/{event-id}/annotation
 */
export async function saveEventAnnotation(params: {
  eventId: string;
  markers: MarkerData[];
  viewport1: ViewportState;
  viewport2: ViewportState;
  beforeMarker?: AnnotationMarker;
  afterMarker?: AnnotationMarker;
}): Promise<ManagementApiResponse> {
  const { eventId, markers, viewport1, viewport2, beforeMarker, afterMarker } = params;

  console.log('💾 [Annotations] Saving event annotation with viewports:', {
    eventId,
    markerCount: markers.length,
    viewport1,
    viewport2,
  });

  const url = `${MANAGEMENT_API_URL}/events/${eventId}/annotation`;
  const body: any = {
    markers,
    viewport1,
    viewport2,
  };

  // Include legacy marker fields for backward compatibility
  if (beforeMarker) {
    body.before_marker = beforeMarker;
  }
  if (afterMarker) {
    body.after_marker = afterMarker;
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: getManagementHeaders(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Management API request failed with status ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ [Annotations] Successfully saved event annotation');

    return {
      success: true,
      message: 'Event annotation saved successfully',
      data,
    };
  } catch (error) {
    console.error('❌ [Annotations] Failed to save event annotation:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

/**
 * Get event annotation
 * GET /management/events/{event-id}/annotation
 */
export async function getEventAnnotation(
  eventId: string
): Promise<ManagementApiResponse> {
  console.log('📖 [Annotations] Getting event annotation:', eventId);

  const url = `${MANAGEMENT_API_URL}/events/${eventId}/annotation`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: getManagementHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Management API request failed with status ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ [Annotations] Successfully retrieved event annotation');

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('❌ [Annotations] Failed to get event annotation:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

/**
 * Batch update event annotations
 * POST /management/events/annotations/batch
 */
export async function batchUpdateAnnotations(updates: Array<{
  eventId: string;
  annotation: {
    markers: MarkerData[];
    viewport1: ViewportState;
    viewport2: ViewportState;
    beforeMarker?: AnnotationMarker;
    afterMarker?: AnnotationMarker;
  };
}>): Promise<ManagementApiResponse> {
  console.log('📦 [Annotations] Batch updating event annotations:', updates.length);

  const url = `${MANAGEMENT_API_URL}/events/annotations/batch`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: getManagementHeaders(),
      body: JSON.stringify({ updates }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Management API request failed with status ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ [Annotations] Successfully batch updated annotations');

    return {
      success: true,
      message: `Batch updated ${data.updated} annotations`,
      data,
    };
  } catch (error) {
    console.error('❌ [Annotations] Failed to batch update annotations:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

/**
 * Get management API status
 */
export function getManagementApiStatus(): {
  configured: boolean;
  baseUrl: string;
} {
  return {
    configured: !!MANAGEMENT_API_KEY,
    baseUrl: MANAGEMENT_API_URL,
  };
}
